import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaVideo, FaStop, FaTrash, FaSave, FaUpload, FaPlay } from "react-icons/fa";
import { FiZap } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { hitBeApi, hitBeApiFormData } from "../../../api/api";
import { appStore } from "../../../store/Store";
import VoiceCreditPaymentModal from "../VoiceCreditPaymentModal";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import {
  useAiFeatureColors,
  AiFeatureGuidePanel,
  AiCreditsBanner,
  AiHeroCard,
  AiStepStrip,
  AiHistorySidebar,
  AiHistoryItem,
} from "../ai/aiFeatureUi";
import {
  fetchScreenListenAssetBlobUrl,
  revokeBlobUrl,
} from "../../../utils/screenListenMedia";

const RECORDINGS_ENDPOINT = "dashboard/video-ai/recordings";
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;
const MAX_PARTS = 6;
const UPLOAD_EXT_MIME = {
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  webm: "video/webm",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  "3gp": "video/3gpp",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
};

/** Best-effort duration (seconds) from an object URL; 0 if unknown. */
function probeVideoDuration(url) {
  return new Promise((resolve) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      const d = Number(el.duration);
      resolve(Number.isFinite(d) && d > 0 ? Math.round(d) : 0);
    };
    el.onerror = () => resolve(0);
    el.src = url;
  });
}

function pickRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return "";
}

function formatTimer(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function VideoAi() {
  const toast = useToast();
  const dispatch = useDispatch();
  const { pageBg, cardBg, borderColor, muted, panelBg, accent } =
    useAiFeatureColors("purple");
  const {
    remaining,
    canUse: canUseVideoAi,
    payOpen,
    setPayOpen,
    promptBuy,
  } = useAiCreditsGate();

  // ---- camera recorder state ------------------------------------------------
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  /** Video parts composed into one box: {blob, url, name, durationSec, source}. */
  const [pendingParts, setPendingParts] = useState([]);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordStartRef = useRef(0);
  const previewRef = useRef(null);

  // ---- history / detail state ----------------------------------------------
  const [recordings, setRecordings] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeRecordingId, setActiveRecordingId] = useState(null);
  const [activeRecording, setActiveRecording] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [frameSrcs, setFrameSrcs] = useState([]);
  const [videoSrcs, setVideoSrcs] = useState({});
  const [loadingVideoIdx, setLoadingVideoIdx] = useState(null);
  const [question, setQuestion] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const applyCredits = useCallback(
    (userTokenDetails) => {
      if (userTokenDetails) {
        const currentUser = appStore.getState().user || {};
        dispatch({
          type: "SET_USER",
          value: { ...currentUser, userTokenDetails },
        });
      }
    },
    [dispatch]
  );

  const loadRecordings = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await hitBeApi(RECORDINGS_ENDPOINT, "GET");
      setRecordings(res?.data?.recordings || []);
    } catch (error) {
      toast({
        title: "Could not load videos",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingList(false);
    }
  }, [toast]);

  useEffect(() => {
    loadRecordings();
  }, [loadRecordings]);

  // Load one recording's detail when selected from history.
  useEffect(() => {
    let cancelled = false;
    setVideoSrcs((prev) => {
      Object.values(prev).forEach(revokeBlobUrl);
      return {};
    });
    if (!activeRecordingId) {
      setActiveRecording(null);
      return undefined;
    }
    setLoadingDetail(true);
    hitBeApi(`${RECORDINGS_ENDPOINT}/${activeRecordingId}`, "GET")
      .then((res) => {
        if (!cancelled) setActiveRecording(res?.data?.recording || null);
      })
      .catch((error) => {
        if (!cancelled) {
          toast({
            title: "Could not load recording",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRecordingId, toast]);

  // Fetch protected timeline thumbnails for the selected recording.
  const timelineCount = activeRecording?.timeline?.length || 0;
  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];
    setFrameSrcs([]);
    if (!activeRecordingId || !timelineCount) return undefined;
    Promise.all(
      Array.from({ length: timelineCount }, (_, i) =>
        fetchScreenListenAssetBlobUrl(
          `${RECORDINGS_ENDPOINT}/${activeRecordingId}/frames/${i}`
        ).catch(() => null)
      )
    ).then((urls) => {
      if (cancelled) {
        urls.forEach(revokeBlobUrl);
        return;
      }
      createdUrls.push(...urls.filter(Boolean));
      setFrameSrcs(urls);
    });
    return () => {
      cancelled = true;
      createdUrls.forEach(revokeBlobUrl);
    };
  }, [activeRecordingId, timelineCount]);

  const loadSegmentVideo = async (segIndex) => {
    if (videoSrcs[segIndex] || loadingVideoIdx !== null) return;
    setLoadingVideoIdx(segIndex);
    try {
      const url = await fetchScreenListenAssetBlobUrl(
        `${RECORDINGS_ENDPOINT}/${activeRecordingId}/segments/${segIndex}/media`
      );
      setVideoSrcs((prev) => ({ ...prev, [segIndex]: url }));
    } catch {
      toast({
        title: "Could not load video",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingVideoIdx(null);
    }
  };

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Live camera preview while recording.
  useEffect(() => {
    if (isRecording && previewRef.current && streamRef.current) {
      previewRef.current.srcObject = streamRef.current;
      previewRef.current.play?.().catch(() => {});
    }
  }, [isRecording]);

  const clearPending = useCallback(() => {
    setPendingParts((prev) => {
      prev.forEach((p) => revokeBlobUrl(p.url));
      return [];
    });
  }, []);

  const removePart = (index) => {
    setPendingParts((prev) => {
      const part = prev[index];
      if (part) revokeBlobUrl(part.url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || isRecording) return;
    if (pendingParts.length + files.length > MAX_PARTS) {
      toast({
        title: "Too many parts",
        description: `A video box can have up to ${MAX_PARTS} videos.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const newParts = [];
    for (const file of files) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const mime = String(file.type || "").toLowerCase();
      const isVideoFile = mime.startsWith("video/") || !!UPLOAD_EXT_MIME[ext];
      if (!isVideoFile) {
        toast({
          title: "Unsupported file skipped",
          description: `${file.name} is not a video file (mp4, webm, mov).`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast({
          title: "File too large — skipped",
          description: `${file.name} exceeds the 200 MB per-file limit.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        continue;
      }
      // Rewrap whenever the reported type is not video/* (e.g. .mov/.mkv often
      // arrive as application/octet-stream) so the server sees a video mime.
      const blob = mime.startsWith("video/")
        ? file
        : new Blob([file], { type: UPLOAD_EXT_MIME[ext] || "video/mp4" });
      const url = URL.createObjectURL(blob);
      newParts.push({
        blob,
        url,
        name: file.name,
        durationSec: await probeVideoDuration(url),
        source: "upload",
      });
    }
    if (!newParts.length) return;
    setPendingParts((prev) => [...prev, ...newParts]);
    setTitle((prev) => prev.trim() || newParts[0].name.replace(/\.[^.]+$/, ""));
  };

  useEffect(
    () => () => {
      clearInterval(timerRef.current);
      stopStream();
    },
    [stopStream]
  );

  const startRecording = async () => {
    if (isRecording) return;
    if (pendingParts.length >= MAX_PARTS) {
      toast({
        title: "Too many parts",
        description: `A video box can have up to ${MAX_PARTS} videos. Save or remove some first.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "video/webm",
        });
        chunksRef.current = [];
        stopStream();
        const elapsed = recordStartRef.current
          ? Math.round((Date.now() - recordStartRef.current) / 1000)
          : 0;
        recordStartRef.current = 0;
        if (blob.size < 2000) {
          toast({
            title: "Recording too short",
            description: "Nothing was captured. Try recording again.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        setPendingParts((prev) => [
          ...prev,
          {
            blob,
            url: URL.createObjectURL(blob),
            name: `Camera recording ${
              prev.filter((p) => p.source === "camera").length + 1
            }`,
            durationSec: elapsed,
            source: "camera",
          },
        ]);
      };
      recorder.onerror = () => stopRecording();
      stream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          if (!mediaRecorderRef.current) return;
          toast({
            title: "Recording interrupted",
            description:
              "The camera was released. The video captured so far was kept — you can save it.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          stopRecording();
        };
      });
      // Timeslice flushes chunks every second so an interruption keeps the
      // video captured up to that point.
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      recordStartRef.current = Date.now();
      setIsRecording(true);
      setRecordSeconds(0);
      timerRef.current = setInterval(() => {
        if (recordStartRef.current) {
          setRecordSeconds(
            Math.round((Date.now() - recordStartRef.current) / 1000)
          );
        }
      }, 1000);
    } catch (error) {
      stopStream();
      toast({
        title: "Camera unavailable",
        description:
          error?.name === "NotAllowedError"
            ? "Allow camera and microphone access in your browser to record."
            : error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setIsRecording(false);
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    try {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      } else {
        stopStream();
      }
    } catch {
      stopStream();
    }
  };

  const saveRecording = async () => {
    if (!pendingParts.length || saving) return;
    setSaving(true);
    try {
      const form = new FormData();
      pendingParts.forEach((part, i) => {
        const ext = part.blob.type.includes("mp4") ? "mp4" : "webm";
        const fileName =
          part.source === "upload" && part.name
            ? part.name
            : `camera-recording-${i + 1}.${ext}`;
        form.append("video", part.blob, fileName);
      });
      form.append(
        "segmentMeta",
        JSON.stringify(
          pendingParts.map((p) => ({
            durationSec: p.durationSec || 0,
            source: p.source,
          }))
        )
      );
      if (title.trim()) form.append("title", title.trim());
      const totalDuration = pendingParts.reduce(
        (sum, p) => sum + (p.durationSec || 0),
        0
      );
      if (totalDuration) form.append("durationSec", totalDuration);
      const res = await hitBeApiFormData("dashboard/video-ai", form);
      const recording = res?.data?.recording;
      toast({
        title: "Video saved",
        description: "Open it and run AI analysis to build the timeline.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      clearPending();
      setTitle("");
      await loadRecordings();
      if (recording?._id) setActiveRecordingId(recording._id);
    } catch (error) {
      toast({
        title: "Could not save video",
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteRecording = async (id) => {
    try {
      await hitBeApi(`${RECORDINGS_ENDPOINT}/${id}`, "DELETE");
      if (id === activeRecordingId) setActiveRecordingId(null);
      setRecordings((prev) => prev.filter((r) => r._id !== id));
    } catch (error) {
      toast({
        title: "Could not delete recording",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const runAnalysis = async (withQuestion, rebuild = false) => {
    if (!activeRecordingId || analyzing) return;
    if (!canUseVideoAi) {
      promptBuy();
      return;
    }
    setAnalyzing(true);
    try {
      const res = await hitBeApi(
        `${RECORDINGS_ENDPOINT}/${activeRecordingId}/analyze`,
        "POST",
        { question: withQuestion ? question.trim() : "", rebuild }
      );
      applyCredits(res?.data?.userTokenDetails);
      if (res?.data?.recording) setActiveRecording(res.data.recording);
      if (withQuestion) setQuestion("");
      loadRecordings();
    } catch (error) {
      toast({
        title: "Analysis failed",
        description: error.message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const isNewActive = !activeRecordingId;
  const analyzed = !!activeRecording?.analyzed;

  return (
    <Box minH="100vh" bg={pageBg} p={{ base: 3, md: 6 }}>
      <AiFeatureGuidePanel
        title="Video AI"
        intro="Record with your camera or upload video files, keep several videos in one box, and let AI build a timeline of what happens at each moment. Then ask questions — is a certain person seen, does a car number appear, what happens at a given time."
        helps={[
          "Record from the camera or upload videos (mp4, webm, mov) — up to 6 in one box.",
          "AI analysis extracts frames over time, describes each moment, and transcribes the audio track.",
          "Read the timeline with thumbnails, and ask questions like 'is a man in a red jacket seen?' or 'which car number plates appear?' — answers cite timestamps.",
          "Everything is saved: videos, timeline, and every Q&A stay in your history.",
        ]}
        theme="purple"
      />

      <AiStepStrip
        theme="purple"
        steps={[
          {
            label: "Record or upload videos",
            hint: "Camera recordings and video files, together in one box.",
          },
          {
            label: "AI builds a timeline",
            hint: "Frames are read over time — actions, people, vehicles, text.",
          },
          {
            label: "Ask about the video",
            hint: "Person seen? Plate number shown? Answers cite timestamps.",
          },
        ]}
      />

      <Flex gap={5} direction={{ base: "column", lg: "row" }} align="start">
        <AiHistorySidebar
          theme="purple"
          title="Video boxes"
          newLabel="+ New video box"
          isNewActive={isNewActive}
          onNew={() => setActiveRecordingId(null)}
          onRefresh={loadRecordings}
          loading={loadingList}
          emptyText="No videos yet. Record or upload your first video and save it."
        >
          {recordings.map((r) => (
            <AiHistoryItem
              key={r._id}
              theme="purple"
              active={r._id === activeRecordingId}
              title={r.title}
              meta={
                <HStack spacing={2}>
                  {r.durationLabel ? (
                    <Badge colorScheme="purple" variant="subtle" fontSize="2xs">
                      {r.durationLabel}
                    </Badge>
                  ) : null}
                  {r.segmentCount > 1 ? (
                    <Badge colorScheme="cyan" variant="subtle" fontSize="2xs">
                      {r.segmentCount} videos
                    </Badge>
                  ) : null}
                  <Text fontSize="2xs" color={muted}>
                    {r.createdAt
                      ? format(new Date(r.createdAt), "d MMM yyyy, h:mm a")
                      : ""}
                  </Text>
                </HStack>
              }
              preview={r.summaryPreview}
              statusBadge={
                r.analyzed ? (
                  <Badge colorScheme="green" fontSize="2xs">
                    analyzed
                  </Badge>
                ) : null
              }
              onSelect={() => setActiveRecordingId(r._id)}
              onDelete={(e) => {
                e.stopPropagation();
                deleteRecording(r._id);
              }}
            />
          ))}
        </AiHistorySidebar>

        <Box flex="1" w="full" minW={0}>
          {isNewActive ? (
            <Box position="relative">
              <AiHeroCard
                theme="purple"
                title="Video AI"
                subtitle="Record from your camera or upload video files, save them as one box, then let AI build a timeline and answer questions about what is seen."
                recording={isRecording}
              >
                <AiCreditsBanner
                  canUse={canUseVideoAi}
                  remaining={remaining}
                  onBuy={promptBuy}
                  variant="hero"
                />
                <HStack spacing={4} flexWrap="wrap">
                  {!isRecording ? (
                    <Button
                      leftIcon={<FaVideo />}
                      colorScheme="whiteAlpha"
                      bg="whiteAlpha.300"
                      _hover={{ bg: "whiteAlpha.400" }}
                      color="white"
                      size="lg"
                      onClick={startRecording}
                    >
                      {pendingParts.length
                        ? "Record another video"
                        : "Record video"}
                    </Button>
                  ) : (
                    <Button
                      leftIcon={<FaStop />}
                      colorScheme="red"
                      size="lg"
                      onClick={stopRecording}
                    >
                      Stop recording
                    </Button>
                  )}
                  {!isRecording ? (
                    <Button
                      leftIcon={<FaUpload />}
                      variant="outline"
                      color="white"
                      borderColor="whiteAlpha.600"
                      _hover={{ bg: "whiteAlpha.300" }}
                      size="lg"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {pendingParts.length
                        ? "Add video files"
                        : "Upload video files"}
                    </Button>
                  ) : null}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="video/*,.mp4,.m4v,.mov,.webm,.mkv,.avi,.3gp,.mpg,.mpeg"
                    style={{ display: "none" }}
                    onChange={onFilePicked}
                  />
                  {isRecording ? (
                    <HStack color="white" spacing={2}>
                      <Box
                        w="10px"
                        h="10px"
                        borderRadius="full"
                        bg="red.400"
                        sx={{
                          animation: "blink 1s ease-in-out infinite",
                          "@keyframes blink": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.25 },
                          },
                        }}
                      />
                      <Text fontWeight="semibold" fontSize="lg">
                        {formatTimer(recordSeconds)}
                      </Text>
                    </HStack>
                  ) : null}
                </HStack>
                {isRecording ? (
                  <Box mt={4} maxW="480px">
                    <video
                      ref={previewRef}
                      muted
                      playsInline
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        background: "black",
                      }}
                    />
                  </Box>
                ) : null}
              </AiHeroCard>

              {pendingParts.length ? (
                <Box
                  mt={5}
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  boxShadow="lg"
                  p={{ base: 4, md: 6 }}
                >
                  <HStack justify="space-between" mb={3} flexWrap="wrap">
                    <Heading size="sm">
                      New video box · {pendingParts.length}{" "}
                      {pendingParts.length === 1 ? "video" : "videos"}
                    </Heading>
                    <Text fontSize="sm" color={muted}>
                      Total{" "}
                      {formatTimer(
                        pendingParts.reduce(
                          (sum, p) => sum + (p.durationSec || 0),
                          0
                        )
                      )}
                    </Text>
                  </HStack>
                  <VStack align="stretch" spacing={3} mb={4}>
                    {pendingParts.map((part, i) => (
                      <Box
                        key={part.url}
                        bg={panelBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        p={3}
                      >
                        <HStack justify="space-between" mb={2} flexWrap="wrap">
                          <HStack spacing={2} minW={0}>
                            <Badge
                              colorScheme={
                                part.source === "upload" ? "purple" : "pink"
                              }
                              variant="subtle"
                              flexShrink={0}
                            >
                              {part.source === "upload" ? "File" : "Camera"}
                            </Badge>
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                              {part.name}
                            </Text>
                            {part.durationSec ? (
                              <Text fontSize="xs" color={muted} flexShrink={0}>
                                {formatTimer(part.durationSec)}
                              </Text>
                            ) : null}
                          </HStack>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            leftIcon={<FaTrash />}
                            onClick={() => removePart(i)}
                            isDisabled={saving}
                          >
                            Remove
                          </Button>
                        </HStack>
                        <video
                          src={part.url}
                          controls
                          playsInline
                          style={{
                            width: "100%",
                            maxHeight: "280px",
                            borderRadius: "8px",
                            background: "black",
                          }}
                        />
                      </Box>
                    ))}
                  </VStack>
                  <Input
                    placeholder="Video box title (e.g. Gate CCTV Tuesday, Site visit walkthrough)"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    mb={4}
                  />
                  <HStack spacing={3}>
                    <Button
                      leftIcon={<FaSave />}
                      colorScheme={accent}
                      onClick={saveRecording}
                      isLoading={saving}
                      loadingText="Uploading"
                      isDisabled={isRecording}
                    >
                      Save video box
                      {pendingParts.length > 1
                        ? ` (${pendingParts.length} videos)`
                        : ""}
                    </Button>
                    <Button
                      leftIcon={<FaTrash />}
                      variant="ghost"
                      colorScheme="red"
                      onClick={clearPending}
                      isDisabled={saving || isRecording}
                    >
                      Discard all
                    </Button>
                  </HStack>
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box
              bg={cardBg}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="lg"
              p={{ base: 4, md: 6 }}
              position="relative"
            >
              {loadingDetail || !activeRecording ? (
                <Flex justify="center" py={16}>
                  <Spinner color={`${accent}.500`} size="lg" />
                </Flex>
              ) : (
                <VStack align="stretch" spacing={5}>
                  <Box>
                    <HStack justify="space-between" flexWrap="wrap" mb={1}>
                      <Heading size="md">{activeRecording.title}</Heading>
                      <HStack>
                        {activeRecording.durationLabel ? (
                          <Badge colorScheme="purple" borderRadius="full" px={3}>
                            {activeRecording.durationLabel}
                          </Badge>
                        ) : null}
                        {analyzed ? (
                          <Badge colorScheme="green" borderRadius="full" px={3}>
                            Analyzed
                          </Badge>
                        ) : null}
                      </HStack>
                    </HStack>
                    <Text fontSize="sm" color={muted}>
                      {activeRecording.createdAt
                        ? format(
                            new Date(activeRecording.createdAt),
                            "d MMM yyyy, h:mm a"
                          )
                        : ""}
                    </Text>
                  </Box>

                  {(activeRecording.segments || []).length ? (
                    <VStack align="stretch" spacing={3}>
                      {(activeRecording.segments || []).map((seg, i) => (
                        <Box
                          key={seg.index ?? i}
                          bg={panelBg}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={borderColor}
                          p={3}
                        >
                          <HStack justify="space-between" flexWrap="wrap" mb={2}>
                            <HStack spacing={2} minW={0}>
                              <Badge
                                colorScheme={
                                  seg.source === "upload" ? "purple" : "pink"
                                }
                                variant="subtle"
                              >
                                Video {i + 1} ·{" "}
                                {seg.source === "upload" ? "File" : "Camera"}
                              </Badge>
                              {seg.fileName ? (
                                <Text fontSize="xs" color={muted} noOfLines={1}>
                                  {seg.fileName}
                                </Text>
                              ) : null}
                              {seg.durationLabel ? (
                                <Text fontSize="xs" color={muted}>
                                  {seg.durationLabel}
                                </Text>
                              ) : null}
                            </HStack>
                            {!videoSrcs[i] ? (
                              <Button
                                size="xs"
                                leftIcon={<FaPlay />}
                                variant="outline"
                                colorScheme={accent}
                                onClick={() => loadSegmentVideo(i)}
                                isLoading={loadingVideoIdx === i}
                              >
                                Load video
                              </Button>
                            ) : null}
                          </HStack>
                          {videoSrcs[i] ? (
                            <video
                              src={videoSrcs[i]}
                              controls
                              playsInline
                              style={{
                                width: "100%",
                                maxHeight: "320px",
                                borderRadius: "8px",
                                background: "black",
                              }}
                            />
                          ) : null}
                        </Box>
                      ))}
                    </VStack>
                  ) : null}

                  {!analyzed ? (
                    <Box
                      bg={panelBg}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={borderColor}
                      p={5}
                      textAlign="center"
                    >
                      <Heading size="sm" mb={2}>
                        Not analyzed yet
                      </Heading>
                      <Text fontSize="sm" color={muted} mb={4}>
                        AI will extract frames across the video, describe what
                        happens at each moment, transcribe the audio, and build
                        a timeline you can question.
                      </Text>
                      <Button
                        leftIcon={<FiZap />}
                        colorScheme={accent}
                        size="lg"
                        onClick={() => runAnalysis(false)}
                        isLoading={analyzing}
                        loadingText="Analyzing video…"
                      >
                        Analyze with AI
                      </Button>
                      {analyzing ? (
                        <Text fontSize="xs" color={muted} mt={3}>
                          Extracting frames and reading them with AI — this can
                          take a minute or two for longer videos.
                        </Text>
                      ) : null}
                    </Box>
                  ) : (
                    <>
                      <Box
                        bg={panelBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        p={4}
                      >
                        <Heading size="xs" mb={2} color={muted}>
                          Ask about this video
                        </Heading>
                        <Textarea
                          placeholder='e.g. "Is a man in a red jacket seen anywhere?", "Which car number plates appear?", "What happens around 2:30?"'
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          rows={2}
                          mb={3}
                          bg={cardBg}
                        />
                        <Button
                          leftIcon={<FiZap />}
                          colorScheme={accent}
                          onClick={() => runAnalysis(true)}
                          isLoading={analyzing}
                          loadingText="Answering"
                          isDisabled={!question.trim()}
                        >
                          Ask AI
                        </Button>
                      </Box>

                      {activeRecording.analyses?.length ? (
                        <VStack align="stretch" spacing={4}>
                          <Heading size="sm">Questions & answers</Heading>
                          {[...activeRecording.analyses].reverse().map((a) => (
                            <Box
                              key={a._id}
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="xl"
                              p={4}
                            >
                              <HStack
                                justify="space-between"
                                mb={2}
                                flexWrap="wrap"
                              >
                                <Badge colorScheme="purple" borderRadius="full">
                                  Q: {a.question || "Full analysis"}
                                </Badge>
                                <Text fontSize="xs" color={muted}>
                                  {a.createdAt
                                    ? format(
                                        new Date(a.createdAt),
                                        "d MMM yyyy, h:mm a"
                                      )
                                    : ""}
                                </Text>
                              </HStack>
                              <Text
                                fontSize="sm"
                                whiteSpace="pre-wrap"
                                lineHeight="tall"
                              >
                                {a.analysis}
                              </Text>
                            </Box>
                          ))}
                        </VStack>
                      ) : null}

                      {activeRecording.summary ? (
                        <Box>
                          <HStack justify="space-between" mb={2} flexWrap="wrap">
                            <Heading size="sm">AI summary</Heading>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme={accent}
                              leftIcon={<FiZap />}
                              onClick={() => runAnalysis(false, true)}
                              isLoading={analyzing}
                            >
                              Re-analyze (rebuild timeline)
                            </Button>
                          </HStack>
                          <Box
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="xl"
                            p={4}
                          >
                            <Text
                              fontSize="sm"
                              whiteSpace="pre-wrap"
                              lineHeight="tall"
                            >
                              {activeRecording.summary}
                            </Text>
                          </Box>
                        </Box>
                      ) : null}

                      {(activeRecording.timeline || []).length ? (
                        <Box>
                          <Heading size="sm" mb={3}>
                            Timeline
                          </Heading>
                          <VStack align="stretch" spacing={3}>
                            {(activeRecording.timeline || []).map((t, i) => (
                              <Flex
                                key={t.index ?? i}
                                gap={3}
                                bg={panelBg}
                                borderRadius="xl"
                                borderWidth="1px"
                                borderColor={borderColor}
                                p={3}
                                direction={{ base: "column", sm: "row" }}
                              >
                                {frameSrcs[i] ? (
                                  <Image
                                    src={frameSrcs[i]}
                                    alt={t.label}
                                    borderRadius="lg"
                                    w={{ base: "100%", sm: "160px" }}
                                    objectFit="cover"
                                    flexShrink={0}
                                  />
                                ) : null}
                                <Box minW={0}>
                                  <HStack spacing={2} mb={1} flexWrap="wrap">
                                    <Badge
                                      colorScheme={accent}
                                      borderRadius="full"
                                    >
                                      {t.label}
                                    </Badge>
                                    {(activeRecording.segments || []).length >
                                    1 ? (
                                      <Badge
                                        colorScheme="cyan"
                                        variant="subtle"
                                        fontSize="2xs"
                                      >
                                        Video {t.segmentIndex + 1}
                                      </Badge>
                                    ) : null}
                                  </HStack>
                                  <Text
                                    fontSize="sm"
                                    lineHeight="tall"
                                    whiteSpace="pre-wrap"
                                  >
                                    {t.description}
                                  </Text>
                                </Box>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      ) : null}

                      {activeRecording.transcript ? (
                        <Box>
                          <Heading size="xs" mb={2} color={muted}>
                            Audio transcript
                          </Heading>
                          <Box
                            bg={panelBg}
                            borderRadius="xl"
                            borderWidth="1px"
                            borderColor={borderColor}
                            p={4}
                            maxH="240px"
                            overflowY="auto"
                          >
                            <Text
                              fontSize="sm"
                              whiteSpace="pre-wrap"
                              lineHeight="tall"
                            >
                              {activeRecording.transcript}
                            </Text>
                          </Box>
                        </Box>
                      ) : null}
                    </>
                  )}
                </VStack>
              )}
            </Box>
          )}
        </Box>
      </Flex>

      <VoiceCreditPaymentModal
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
        variant={canUseVideoAi ? "topUp" : "exhausted"}
      />
    </Box>
  );
}
